/**
 * Local Invoices Service
 * Manages draft invoices saved to the AAS local database before they are approved and sent to NetSuite.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface DraftLineItem {
  itemId: string;
  description?: string;
  rate: number;
  quantity: number;
}

export interface LocalInvoice {
  id: string;
  clientId: string;
  clientName?: string;
  netSuiteInvoiceId?: string;
  netSuiteInvoiceNumber?: string;
  periodStart: string;  // "YYYY-MM-DD"
  periodEnd: string;
  totalAmount: number;
  status: 'Draft' | 'Sent' | 'Voided';
  isAutoGenerated: boolean;
  createdAt: string;
  sentAt?: string;
  /** UTC timestamp of the most recent invoice email; absent when never emailed. */
  lastEmailedAt?: string;
  /** Times this invoice has been emailed (auto-send on approval counts as one). */
  emailSendCount: number;
  /** Recipient count of the most recent email send. */
  lastEmailRecipientCount: number;
  lines: DraftLineItem[];
}

export interface CreateDraftRequest {
  netSuiteCustomerId?: string;
  periodStart: string;
  periodEnd: string;
  totalAmount: number;
  customFormId?: string;
  departmentId?: string;
  locationId?: string;
  termsId?: string;
  startDate: string;
  endDate: string;
  memo?: string;
  lines: DraftLineItem[];
}

/**
 * One row per service, state (MVR only) AND rate charged — so a tiered service billed at more
 * than one rate in the period returns several rows, one per band it crossed.
 */
export interface RunningTotalBreakdown {
  serviceType: string;
  stateCode?: string;
  count: number;
  amount: number;
  /** The rate these requests were actually charged. count x price === amount, exactly. */
  price: number;
}

export interface ClientRunningTotal {
  clientId: string;
  clientName: string;
  netSuiteCustomerId?: string;
  periodStart: string;
  periodEnd: string;
  totalAmount: number;
  breakdown: RunningTotalBreakdown[];
}

export const listInvoices = async (status?: string): Promise<LocalInvoice[]> => {
  const url = status
    ? `${API_ENDPOINTS.INVOICES.LIST}?status=${encodeURIComponent(status)}`
    : API_ENDPOINTS.INVOICES.LIST;
  const r = await makeAuthenticatedRequest(url);
  if (!r.ok) throw await createApiError(r);
  return r.json();
};

export const createDraftInvoice = async (req: CreateDraftRequest): Promise<LocalInvoice> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.CREATE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!r.ok) throw await createApiError(r);
  return r.json();
};

export const approveInvoice = async (id: string): Promise<LocalInvoice> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.APPROVE(id), { method: 'POST' });
  if (!r.ok) throw await createApiError(r);
  return r.json();
};

export const deleteInvoice = async (id: string): Promise<void> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.DELETE(id), { method: 'DELETE' });
  if (!r.ok) throw await createApiError(r);
};

export const getRunningTotals = async (): Promise<ClientRunningTotal[]> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.RUNNING_TOTALS);
  if (!r.ok) throw await createApiError(r);
  return r.json();
};

/** Same one-row-per-rate split as RunningTotalBreakdown — this is what invoice lines are built from. */
export interface MonthlyActivityBreakdown {
  serviceType: string;
  stateCode?: string | null;
  count: number;
  totalCost: number;
  /** The rate these requests were actually charged. Use this as the line rate — never totalCost/count. */
  unitPrice: number;
}

export interface MonthlyActivity {
  clientId: string;
  clientName: string;
  netSuiteCustomerId?: string;
  month: string;
  periodStart: string;
  periodEnd: string;
  totalCost: number;
  breakdown: MonthlyActivityBreakdown[];
}

export const getMonthlyActivity = async (netSuiteCustomerId: string, month: string): Promise<MonthlyActivity> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.MONTHLY_ACTIVITY(netSuiteCustomerId, month));
  if (!r.ok) throw await createApiError(r);
  return r.json();
};

export async function sendInvoiceEmail(id: string): Promise<{
  sent: boolean;
  recipientCount: number;
  lastEmailedAt?: string;
  emailSendCount: number;
}> {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.SEND_EMAIL(id), { method: 'POST' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).error || 'Failed to send invoice email');
  }
  return response.json();
}

export interface AutoInvoicingRunResult {
  date: string;
  draftsCreated: number;
  submittedToNetSuite: number;
  skippedNoActivity: number;
  skippedAlreadyExists: number;
  skippedNotDue: number;
  errors: number;
  messages: string[];
}

/** Manually triggers the auto-invoicing run for a given date (defaults to today). SystemAdmin only. */
export const triggerAutoInvoicing = async (date?: string): Promise<AutoInvoicingRunResult> => {
  const r = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICES.AUTO_INVOICE_RUN(date), { method: 'POST' });
  if (!r.ok) throw await createApiError(r);
  return r.json();
};
